import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/themes.css';

// ── 21 Thèmes ─────────────────────────────────────────────────────────────────
const THEME_CATEGORIES = [
  {
    id: 'weather', label: '🌤️ Météo',
    themes: [
      { id: 'default', label: 'Nuit',    icon: '🌙', css: '',             swatch: 'linear-gradient(135deg,#0f172a,#1e293b)',                        particles: null     },
      { id: 'sunny',   label: 'Soleil',  icon: '☀️', css: 'theme-sunny',  swatch: 'linear-gradient(135deg,#451a03,#92400e,#0f172a)',                 particles: null     },
      { id: 'rain',    label: 'Pluie',   icon: '🌧️', css: 'theme-rain',   swatch: 'linear-gradient(135deg,#1e3a8a,#0c1445,#0f172a)',                 particles: 'rain'   },
      { id: 'fog',     label: 'Brume',   icon: '🌫️', css: 'theme-fog',    swatch: 'linear-gradient(135deg,#334155,#475569,#1e293b)',                 particles: null     },
      { id: 'storm',   label: 'Orage',   icon: '⛈️', css: 'theme-storm',  swatch: 'linear-gradient(135deg,#2e1065,#4c1d95,#0f172a)',                 particles: null     },
      { id: 'snow',    label: 'Hiver',   icon: '❄️', css: 'theme-snow',   swatch: 'linear-gradient(135deg,#082f49,#0c4a6e,#0f172a)',                 particles: 'snow'   },
    ],
  },
  {
    id: 'sky', label: '🌌 Ciel & Cosmos',
    themes: [
      { id: 'dawn',    label: 'Aube',    icon: '🌄', css: 'theme-dawn',    swatch: 'linear-gradient(135deg,#431407,#c2410c,#fbbf24,#0f172a)', particles: null    },
      { id: 'sunset',  label: 'Crépusc', icon: '🌅', css: 'theme-sunset',  swatch: 'linear-gradient(135deg,#4a044e,#9d174d,#7c3aed,#0f172a)', particles: null    },
      { id: 'aurora',  label: 'Boréale', icon: '🌠', css: 'theme-aurora',  swatch: 'linear-gradient(135deg,#022c22,#065f46,#4c1d95,#0f172a)', particles: 'aurora'},
      { id: 'galaxy',  label: 'Galaxie', icon: '🌌', css: 'theme-galaxy',  swatch: 'linear-gradient(135deg,#1e1b4b,#312e81,#4c1d95,#0f172a)', particles: 'stars' },
      { id: 'moon',    label: 'Lune',    icon: '🌕', css: 'theme-moon',    swatch: 'linear-gradient(135deg,#0f172a,#1e293b,#334155,#0f172a)', particles: 'stars' },
      { id: 'neon',    label: 'Néon',    icon: '💜', css: 'theme-neon',    swatch: 'linear-gradient(135deg,#581c87,#0f766e,#ea580c)',          particles: null    },
    ],
  },
  {
    id: 'ocean', label: '🌊 Océans & Eau',
    themes: [
      { id: 'ocean',  label: 'Océan',   icon: '🌊', css: 'theme-ocean',  swatch: 'linear-gradient(135deg,#0c4a6e,#0e7490,#0f172a)', particles: null    },
      { id: 'coral',  label: 'Corail',  icon: '🐚', css: 'theme-coral',  swatch: 'linear-gradient(135deg,#164e63,#be185d,#0e7490)', particles: null    },
      { id: 'abyss',  label: 'Abysses', icon: '🐋', css: 'theme-abyss',  swatch: 'linear-gradient(135deg,#020617,#0c1445,#012030)', particles: 'stars' },
    ],
  },
  {
    id: 'nature', label: '🌿 Nature & Terre',
    themes: [
      { id: 'nature',  label: 'Forêt',   icon: '🌿', css: 'theme-nature',  swatch: 'linear-gradient(135deg,#052e16,#064e3b,#0f172a)',          particles: null      },
      { id: 'jungle',  label: 'Jungle',  icon: '🌴', css: 'theme-jungle',  swatch: 'linear-gradient(135deg,#052e16,#166534,#3f6212,#0f172a)',  particles: 'firefly' },
      { id: 'sakura',  label: 'Sakura',  icon: '🌸', css: 'theme-sakura',  swatch: 'linear-gradient(135deg,#500724,#9d174d,#fce7f3,#0f172a)',  particles: 'sakura'  },
      { id: 'autumn',  label: 'Automne', icon: '🍂', css: 'theme-autumn',  swatch: 'linear-gradient(135deg,#431407,#92400e,#7f1d1d,#0f172a)',  particles: 'leaves'  },
      { id: 'desert',  label: 'Désert',  icon: '🏜️', css: 'theme-desert',  swatch: 'linear-gradient(135deg,#451a03,#92400e,#a16207,#0f172a)',  particles: null      },
      { id: 'volcano', label: 'Volcan',  icon: '🌋', css: 'theme-volcano', swatch: 'linear-gradient(135deg,#1c0000,#450a0a,#991b1b,#0f172a)',  particles: null      },
    ],
  },
];

const ALL_THEMES  = THEME_CATEGORIES.flatMap(c => c.themes);
const ALL_CSS     = ALL_THEMES.map(t => t.css).filter(Boolean);
const THEME_KEY   = 'phonelab_theme_admin';
const SESSION_KEY = 'phonelab_theme_public';

// ── Images HD en WebP (40% plus léger que JPEG — non bloquant pour le SEO) ────
// Format: fm=webp&q=70&w=1920 → taille ~250-400 Ko par image
const THEME_IMAGES = {
  default: 'https://images.unsplash.com/photo-1746282582853-9e846461506d?auto=format&fm=webp&q=70&w=1920',
  sunny:   'https://images.unsplash.com/photo-1774640778352-e79f5df42205?auto=format&fm=webp&q=70&w=1920',
  rain:    'https://images.unsplash.com/photo-1760115290868-68e56a711bb7?auto=format&fm=webp&q=70&w=1920',
  fog:     'https://images.unsplash.com/photo-1765833218088-661d0f672b76?auto=format&fm=webp&q=70&w=1920',
  storm:   'https://images.unsplash.com/photo-1763568972598-02eeb63efce1?auto=format&fm=webp&q=70&w=1920',
  snow:    'https://images.unsplash.com/photo-1768274881004-ffe0b16caf08?auto=format&fm=webp&q=70&w=1920',
  dawn:    'https://images.unsplash.com/photo-1563441142323-f0574bdf0a45?auto=format&fm=webp&q=70&w=1920',
  sunset:  'https://images.unsplash.com/photo-1765993983039-d4bd434842be?auto=format&fm=webp&q=70&w=1920',
  aurora:  'https://images.unsplash.com/photo-1768981931270-3a9c2dd337cc?auto=format&fm=webp&q=70&w=1920',
  galaxy:  'https://images.unsplash.com/photo-1599218444080-13e72efba4b6?auto=format&fm=webp&q=70&w=1920',
  moon:    'https://images.unsplash.com/photo-1771184464810-e588b370e445?auto=format&fm=webp&q=70&w=1920',
  neon:    'https://images.unsplash.com/photo-1771804359822-1ac852e796a5?auto=format&fm=webp&q=70&w=1920',
  ocean:   'https://images.unsplash.com/photo-1589363794163-c428490365c7?auto=format&fm=webp&q=70&w=1920',
  coral:   'https://images.unsplash.com/photo-1698813456278-a7df3ea9cafc?auto=format&fm=webp&q=70&w=1920',
  abyss:   'https://images.unsplash.com/photo-1589363794163-c428490365c7?auto=format&fm=webp&q=70&w=1920',
  nature:  'https://images.unsplash.com/photo-1735666498436-d478930872ab?auto=format&fm=webp&q=70&w=1920',
  jungle:  'https://images.unsplash.com/photo-1659431245880-4e9ad1225f1d?auto=format&fm=webp&q=70&w=1920',
  sakura:  'https://images.unsplash.com/photo-1760954082304-7a45cefcee73?auto=format&fm=webp&q=70&w=1920',
  autumn:  'https://images.unsplash.com/photo-1544645182-95750e7c4214?auto=format&fm=webp&q=70&w=1920',
  desert:  'https://images.unsplash.com/photo-1768207750853-adfafdc6bbc8?auto=format&fm=webp&q=70&w=1920',
  volcano: 'https://images.unsplash.com/photo-1666091863721-54331a5db52d?auto=format&fm=webp&q=70&w=1920',
};

// ── Météo → Thème ─────────────────────────────────────────────────────────────
function weatherCodeToTheme(code) {
  const n = parseInt(code, 10);
  if ([113, 116].includes(n))                                                                           return 'sunny';
  if ([386, 389, 392, 395].includes(n))                                                                return 'storm';
  if ([227, 230, 323, 326, 329, 332, 335, 338, 350, 362, 365, 368, 371, 374, 377].includes(n))        return 'snow';
  if ([176, 185, 263, 266, 281, 284, 293, 296, 299, 302, 305, 308, 311, 314, 317, 320, 353, 356, 359].includes(n)) return 'rain';
  if ([143, 248, 260].includes(n))                                                                     return 'fog';
  return null;
}
async function detectWeather() {
  try {
    const r = await fetch('https://wttr.in/?format=j1', { signal: AbortSignal.timeout(5000) });
    const d = await r.json();
    const code = d.current_condition?.[0]?.weatherCode;
    return code ? weatherCodeToTheme(code) : null;
  } catch { return null; }
}

// ── Préchargement d'image (priorité basse — non bloquant) ─────────────────────
function preloadImage(url) {
  if (!url) return;
  const img = new Image();
  // Charger en basse priorité pour ne pas impacter les Core Web Vitals
  if ('fetchPriority' in img) img.fetchPriority = 'low';
  img.src = url;
}

// ── Particules ────────────────────────────────────────────────────────────────
function spawnParticles(type, el) {
  el.innerHTML = '';
  const mk = (cls, txt, css) => { const d = document.createElement('div'); d.className = cls; if (txt) d.textContent = txt; d.style.cssText = css; el.appendChild(d); };
  if (type === 'snow')    for (let i=0;i<24;i++) mk('snowflake','❄',`left:${Math.random()*100}vw;font-size:${.5+Math.random()*.9}em;animation-delay:${Math.random()*8}s;animation-duration:${6+Math.random()*8}s`);
  if (type === 'rain')    for (let i=0;i<50;i++) mk('raindrop','',`left:${Math.random()*100}vw;height:${20+Math.random()*50}px;animation-delay:${Math.random()*3}s;animation-duration:${.5+Math.random()*.7}s`);
  if (type === 'stars')   for (let i=0;i<70;i++) { const s=1+Math.random()*2.5; mk('starparticle','',`left:${Math.random()*100}vw;top:${Math.random()*85}vh;width:${s}px;height:${s}px;animation-delay:${Math.random()*5}s;animation-duration:${1.5+Math.random()*3}s`); }
  if (type === 'sakura')  { const p=['✿','❀','✾','❁','✽']; for(let i=0;i<22;i++) mk('sakura-petal',p[i%p.length],`left:${Math.random()*100}vw;font-size:${.8+Math.random()}em;animation-delay:${Math.random()*10}s;animation-duration:${7+Math.random()*7}s`); }
  if (type === 'leaves')  { const l=['🍂','🍁','🍃']; for(let i=0;i<18;i++) mk('autumn-leaf',l[i%l.length],`left:${Math.random()*100}vw;font-size:${.8+Math.random()}em;animation-delay:${Math.random()*10}s;animation-duration:${6+Math.random()*8}s`); }
  if (type === 'firefly') for (let i=0;i<18;i++) { const s=3+Math.random()*4; mk('firefly','',`left:${Math.random()*100}vw;top:${30+Math.random()*60}vh;width:${s}px;height:${s}px;animation-delay:${Math.random()*6}s;animation-duration:${2+Math.random()*4}s`); }
  if (type === 'aurora')  for (let i=0;i<14;i++) mk('starparticle','',`left:${Math.random()*100}vw;top:${Math.random()*50}vh;width:${1+Math.random()*2}px;height:${1+Math.random()*2}px;animation-delay:${Math.random()*5}s;animation-duration:${1.5+Math.random()*3}s`);
}

// ── Composant ─────────────────────────────────────────────────────────────────
export function ThemeSwitcher() {
  const location = useLocation();
  const isAdmin  = location.pathname.startsWith('/admin');

  const [open,     setOpen]     = useState(false);
  const [activeId, setActiveId] = useState('default');

  const particleRef = useRef(null);
  const panelRef    = useRef(null);
  const fadeRef     = useRef(null);   // overlay de transition
  const timerRef    = useRef(null);   // timer public

  // ── Appliquer un thème ─────────────────────────────────────────────────────
  const applyTheme = useCallback((id) => {
    const theme = ALL_THEMES.find(t => t.id === id) || ALL_THEMES[0];
    ALL_CSS.forEach(c => document.body.classList.remove(c));
    if (theme.css) document.body.classList.add(theme.css);
    setActiveId(id);
    if (particleRef.current) {
      theme.particles ? spawnParticles(theme.particles, particleRef.current) : (particleRef.current.innerHTML = '');
    }
  }, []);

  // ── Transition fondue (non bloquante) ──────────────────────────────────────
  const switchWithFade = useCallback((newId) => {
    const fade = fadeRef.current;
    if (!fade) { applyTheme(newId); return; }
    // 1. Fondu au noir (350ms)
    fade.style.transition = 'opacity 0.35s ease';
    fade.style.opacity    = '1';
    setTimeout(() => {
      // 2. Changer le thème (image change derrière le voile)
      applyTheme(newId);
      // 3. Révéler le nouveau fond (400ms)
      fade.style.transition = 'opacity 0.4s ease';
      fade.style.opacity    = '0';
    }, 380);
  }, [applyTheme]);

  // ── Thème aléatoire (différent du courant) ─────────────────────────────────
  const pickRandom = useCallback((currentId) => {
    const pool = ALL_THEMES.filter(t => t.id !== 'default' && t.id !== currentId);
    return pool[Math.floor(Math.random() * pool.length)];
  }, []);

  // ── Timer public : change toutes les 30–60 secondes ───────────────────────
  const startPublicTimer = useCallback((initialId) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    let current = initialId;

    const schedule = () => {
      // Intervalle aléatoire entre 30 et 60 secondes
      const delay = 30000 + Math.random() * 30000;
      timerRef.current = setTimeout(() => {
        const next = pickRandom(current);
        current = next.id;
        sessionStorage.setItem(SESSION_KEY, current);
        switchWithFade(current);
        // Précharger le thème suivant (basse priorité) pendant l'affichage
        const nextNext = pickRandom(current);
        preloadImage(THEME_IMAGES[nextNext.id]);
        schedule(); // planifier le prochain changement
      }, delay);
    };
    schedule();
  }, [pickRandom, switchWithFade]);

  // ── Initialisation ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (isAdmin) {
      // Admin : charger le thème sauvegardé
      applyTheme(localStorage.getItem(THEME_KEY) || 'default');
    } else {
      // Public : météo locale → aléatoire en fallback → timer toutes les 30–60s
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) {
        applyTheme(cached);
        // Précharger le prochain thème tout de suite
        const next = pickRandom(cached);
        preloadImage(THEME_IMAGES[next.id]);
        startPublicTimer(cached);
      } else {
        // Afficher d'abord le thème par défaut (déjà en cache navigateur)
        applyTheme('default');
        // Détecter la météo en arrière-plan
        detectWeather().then(wId => {
          const chosen = wId || pickRandom('default').id;
          sessionStorage.setItem(SESSION_KEY, chosen);
          // Attendre que l'image soit disponible avant de changer (~500ms)
          const img = new Image();
          img.src = THEME_IMAGES[chosen] || '';
          const doSwitch = () => { switchWithFade(chosen); startPublicTimer(chosen); };
          img.onload  = doSwitch;
          img.onerror = doSwitch; // changer de toute façon si erreur
        });
      }
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // ── Fermer le panneau au clic extérieur ───────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // ── Sélection manuelle (admin) ─────────────────────────────────────────────
  const selectTheme = useCallback((id) => {
    if (isAdmin) localStorage.setItem(THEME_KEY, id);
    applyTheme(id);
    setOpen(false);
  }, [isAdmin, applyTheme]);

  const activeTheme = ALL_THEMES.find(t => t.id === activeId) || ALL_THEMES[0];

  return (
    <>
      {/* Conteneur particules */}
      <div
        ref={particleRef}
        style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}
        aria-hidden="true"
      />

      {/* Voile de transition fondue (invisible sauf pendant le changement de thème) */}
      <div
        ref={fadeRef}
        style={{
          position: 'fixed', inset: 0,
          zIndex: 9998,
          pointerEvents: 'none',
          background: 'rgba(0,0,0,0.97)',
          opacity: 0,
        }}
        aria-hidden="true"
      />

      {/* Bouton flottant — admin uniquement */}
      {isAdmin && (
        <div ref={panelRef} className="fixed bottom-6 right-6 z-[10001]" data-testid="theme-switcher-wrapper">
          <button
            onClick={() => setOpen(v => !v)}
            data-testid="theme-switcher-btn"
            title="Changer le thème visuel"
            className="flex items-center gap-2 pl-3 pr-3 py-2.5 rounded-full shadow-2xl border border-white/10 backdrop-blur-xl transition-all duration-200 hover:scale-105 hover:border-white/20 active:scale-95"
            style={{
              background: 'rgba(15,23,42,0.92)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
            }}
          >
            <span style={{
              width: 14, height: 14, borderRadius: '50%',
              background: activeTheme.swatch, display: 'inline-block', flexShrink: 0,
              boxShadow: '0 0 6px rgba(255,255,255,0.15)',
            }} />
            <span className="text-white/75 text-xs font-semibold tracking-wide select-none">{activeTheme.icon} {activeTheme.label}</span>
          </button>

          {open && (
            <div
              data-testid="theme-panel"
              style={{
                position: 'absolute', bottom: 'calc(100% + 10px)', right: 0,
                width: 318, maxHeight: '82vh', overflowY: 'auto',
                background: 'rgba(7,10,20,0.98)', backdropFilter: 'blur(32px)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 -4px 80px rgba(0,0,0,0.8)',
                borderRadius: 18, zIndex: 10002,
              }}
            >
              {/* Header */}
              <div className="sticky top-0 flex items-center justify-between px-4 pt-3 pb-2.5"
                style={{ background: 'rgba(7,10,20,0.98)', borderBottom: '1px solid rgba(255,255,255,0.06)', borderRadius: '18px 18px 0 0', zIndex: 2 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '.1em' }}>Thème visuel</span>
                <button
                  onClick={() => { const n = pickRandom(activeId); selectTheme(n.id); }}
                  data-testid="theme-random-btn"
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all hover:scale-105 active:scale-95"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}
                >🎲 Aléatoire</button>
              </div>

              {/* Catégories */}
              <div className="px-3 py-2.5 space-y-3">
                {THEME_CATEGORIES.map(cat => (
                  <div key={cat.id}>
                    <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6, paddingLeft: 2 }}>{cat.label}</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {cat.themes.map(theme => {
                        const active = activeId === theme.id;
                        return (
                          <button key={theme.id} onClick={() => selectTheme(theme.id)} data-testid={`theme-option-${theme.id}`}
                            className="flex flex-col items-center gap-1 p-1.5 rounded-xl border transition-all duration-150 hover:scale-[1.04] active:scale-95"
                            style={{ background: active ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)', borderColor: active ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.06)' }}
                          >
                            <div style={{
                              width: '100%', height: 28, borderRadius: 8, background: theme.swatch,
                              border: active ? '2px solid rgba(255,255,255,0.4)' : '1.5px solid rgba(255,255,255,0.07)',
                              boxShadow: active ? '0 0 18px rgba(255,255,255,0.1)' : 'none', transition: 'all .15s',
                              position: 'relative', overflow: 'hidden',
                            }}>
                              {active && <div style={{ position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.18)' }}><span style={{ fontSize:10,color:'white' }}>✓</span></div>}
                            </div>
                            <span style={{ fontSize: 9.5, color: active ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.42)', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap' }}>
                              {theme.icon} {theme.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <p style={{ textAlign: 'center', fontSize: 9, color: 'rgba(255,255,255,0.15)', paddingBottom: 4 }}>
                  {ALL_THEMES.length} thèmes · Photos HD WebP
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default ThemeSwitcher;
